/**
 * Product Telemetry & Usage Analytics — Pack 13.J.
 *
 * Owning task: Task #1106.
 *
 * Persistence layer for the privacy-conscious telemetry layer defined in
 * `shared/telemetry-events.ts`. Two tables:
 *
 *   - `telemetry_events` — append-only event log keyed by canonical
 *     `event_name` (see `TELEMETRY_EVENT_KEYS`). Carries only tenant + role
 *     context — never user identifiers (PII discipline enforced both at
 *     emit-site in `client/src/lib/telemetry.ts` and at ingest-site in
 *     `server/routes/telemetry.ts`). The `properties` jsonb column carries
 *     the event-specific scrubbed payload.
 *
 *   - `tenant_telemetry_settings` — per-tenant opt-out flag. Honoured by
 *     both the client emitter (refuses to send) and the server ingester
 *     (refuses to persist). When no row exists the default is `enabled`.
 */
import { pgTable, varchar, text, timestamp, jsonb, boolean, index } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants } from "./common";

export const telemetryEvents = pgTable(
  "telemetry_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    /**
     * Tenant scope. Nullable for unauthed page views fired before login
     * (the cookie-consent banner permits a tightly-bounded set of "no
     * tenant context yet" events: `app.page_view`, `app.session_start`,
     * `auth.login_started`).
     */
    tenantId: text("tenant_id"),
    /**
     * Coarse role bucket for the actor. Nullable when no tenant context.
     * Stored as `super_admin` / `tenant_admin` / `worker` / `partner` /
     * `viewer` / `other` per the canonical bucketing in
     * `shared/telemetry-events.ts:roleBucketFromRole`.
     */
    actorRoleBucket: text("actor_role_bucket"),
    /** Canonical event key from TELEMETRY_EVENT_KEYS. */
    eventName: text("event_name").notNull(),
    /**
     * Random per-browser-session identifier (UUID v4). Re-rolled on every
     * page reload / new tab so it cannot be used to re-identify a user
     * across sessions; combined with `event_name` it lets us count unique
     * sessions per feature without recording user IDs.
     */
    sessionId: text("session_id").notNull(),
    /**
     * Application surface where the event fired (route path with query
     * string + fragment stripped). Free-text; the client emitter strips
     * known PII-bearing path parameters before send.
     */
    surface: text("surface"),
    /** Scrubbed event-specific payload. Must NEVER contain user IDs/email. */
    properties: jsonb("properties").$type<Record<string, unknown>>().notNull().default({}),
    occurredAt: timestamp("occurred_at", { withTimezone: true }).defaultNow().notNull(),
    receivedAt: timestamp("received_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (t) => ({
    idxTelemetryEventsTenantOccurred: index("idx_telemetry_events_tenant_occurred").on(
      t.tenantId,
      t.occurredAt,
    ),
    idxTelemetryEventsEventOccurred: index("idx_telemetry_events_event_occurred").on(
      t.eventName,
      t.occurredAt,
    ),
    idxTelemetryEventsSession: index("idx_telemetry_events_session").on(t.sessionId),
  }),
);

export const insertTelemetryEventSchema = createInsertSchema(telemetryEvents).omit({
  id: true,
  receivedAt: true,
});
export type InsertTelemetryEvent = z.infer<typeof insertTelemetryEventSchema>;
export type TelemetryEvent = typeof telemetryEvents.$inferSelect;

/**
 * Per-tenant opt-out for product telemetry collection.
 *
 * When `enabled = false` the platform must not collect telemetry for
 * any actor whose active tenant resolves to this row. Both the client
 * emitter (after fetching `/api/telemetry/opt-out`) and the server
 * ingester (before persisting) honour the flag. Default is enabled.
 */
export const tenantTelemetrySettings = pgTable("tenant_telemetry_settings", {
  tenantId: text("tenant_id").primaryKey().references(() => tenants.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(true),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});
export const insertTenantTelemetrySettingSchema = createInsertSchema(tenantTelemetrySettings).omit({
  updatedAt: true,
});
export type InsertTenantTelemetrySetting = z.infer<typeof insertTenantTelemetrySettingSchema>;
export type TenantTelemetrySetting = typeof tenantTelemetrySettings.$inferSelect;
